import React, { useState, useEffect, useCallback } from 'react';
import { api, formatApiError } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Upload, FileArrowUp, Check, Trash, Clock, CheckCircle, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function BankFeeds() {
  const [clients, setClients] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState('');
  const [uploading, setUploading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [clientsRes, feedsRes] = await Promise.all([
        api.get('/clients'),
        api.get('/bank-feeds'),
      ]);
      setClients(clientsRes.data);
      setFeeds(feedsRes.data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedClient) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await api.post(
        `/bank-feeds/parse?client_id=${selectedClient}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setPreviewData(data);
      toast.success(`Parsed ${data.count} transactions from ${data.filename}`);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleConfirmImport = async () => {
    if (!previewData?.feed_id) return;
    setConfirming(true);

    try {
      const { data } = await api.post(`/bank-feeds/${previewData.feed_id}/confirm`);
      toast.success(data.message);
      setPreviewData(null);
      setUploadOpen(false);
      setSelectedClient('');
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setConfirming(false);
    }
  };

  const handleDeleteFeed = async (feedId) => {
    if (!window.confirm('Delete this feed and its imported transactions?')) return;
    try {
      await api.delete(`/bank-feeds/${feedId}`);
      toast.success('Bank feed deleted');
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleCancelPreview = () => {
    setPreviewData(null);
  };

  const getClientName = (clientId) => {
    const client = clients.find((c) => c.id === clientId);
    return client?.name || 'Unknown';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  return (
    <div className="space-y-6" data-testid="bank-feeds-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="heading-2 mb-1">Bank Feeds</h1>
          <p className="text-muted-foreground">Import bank transactions from CSV, OFX, or QIF files</p>
        </div>
        <Dialog open={uploadOpen} onOpenChange={(open) => {
          setUploadOpen(open);
          if (!open) {
            setPreviewData(null);
            setSelectedClient('');
          }
        }}>
          <DialogTrigger asChild>
            <Button data-testid="import-feed-btn">
              <FileArrowUp className="w-4 h-4 mr-2" />
              Import Bank Feed
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Import Bank Feed</DialogTitle>
              <DialogDescription>
                Upload your bank's CSV, OFX, or QIF export file. Transactions will be parsed for preview before import.
              </DialogDescription>
            </DialogHeader>

            {!previewData ? (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Client</Label>
                  <Select value={selectedClient} onValueChange={setSelectedClient}>
                    <SelectTrigger data-testid="feed-client-select">
                      <SelectValue placeholder="Select a client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name} {client.company_name ? `(${client.company_name})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Bank Export File</Label>
                  <div className="border-2 border-dashed border-border rounded-md p-8 text-center">
                    <input
                      type="file"
                      id="feed-upload"
                      accept=".csv,.ofx,.qfx,.qif"
                      onChange={handleFileUpload}
                      className="hidden"
                      disabled={!selectedClient || uploading}
                      data-testid="feed-file-input"
                    />
                    <FileArrowUp className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <Button
                      variant="outline"
                      onClick={() => document.getElementById('feed-upload').click()}
                      disabled={!selectedClient || uploading}
                      data-testid="select-feed-file-btn"
                    >
                      {uploading ? 'Parsing file...' : 'Select File'}
                    </Button>
                    <p className="text-sm text-muted-foreground mt-2">
                      CSV, OFX, QFX, or QIF format
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Export this file from your bank's online banking portal
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4 py-4" data-testid="feed-preview-section">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-lg">Preview: {previewData.filename}</h3>
                    <p className="text-sm text-muted-foreground">
                      {previewData.count} transactions found ({previewData.format.toUpperCase()} format)
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={handleCancelPreview}
                      disabled={confirming}
                      data-testid="cancel-import-btn"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleConfirmImport}
                      disabled={confirming || previewData.count === 0}
                      data-testid="confirm-import-btn"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      {confirming ? 'Importing...' : `Import ${previewData.count} Transactions`}
                    </Button>
                  </div>
                </div>

                {previewData.count === 0 ? (
                  <div className="text-center py-8 border rounded-md">
                    <Warning className="w-10 h-10 text-amber-500 mx-auto mb-2" />
                    <p className="text-muted-foreground">No transactions could be parsed from this file.</p>
                    <p className="text-sm text-muted-foreground mt-1">Please check the file format and try again.</p>
                  </div>
                ) : (
                  <div className="border rounded-md overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="table-header">
                          <TableHead className="w-10">#</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Reference</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {previewData.transactions.slice(0, 50).map((txn, idx) => (
                          <TableRow key={`${txn.transaction_date}-${txn.amount}-${idx}`} data-testid={`preview-row-${idx}`}>
                            <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
                            <TableCell>{txn.transaction_date || '-'}</TableCell>
                            <TableCell className="max-w-xs truncate">{txn.description || '-'}</TableCell>
                            <TableCell>{txn.reference || '-'}</TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={txn.transaction_type === 'credit' ? 'text-green-600' : 'text-red-600'}
                              >
                                {txn.transaction_type}
                              </Badge>
                            </TableCell>
                            <TableCell className={`text-right ${txn.transaction_type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                              {txn.transaction_type === 'credit' ? '+' : '-'}{formatCurrency(txn.amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {previewData.count > 50 && (
                      <div className="text-center py-2 text-sm text-muted-foreground border-t">
                        Showing first 50 of {previewData.count} transactions
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Import History */}
      <Card className="border border-border">
        <CardHeader className="pb-4">
          <CardTitle className="heading-4 flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Import History
          </CardTitle>
          <CardDescription>
            Previously imported bank feed files
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : feeds.length === 0 ? (
            <div className="text-center py-12">
              <FileArrowUp className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No bank feeds imported yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Click "Import Bank Feed" to upload your bank's CSV, OFX, or QIF export
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>File</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Format</TableHead>
                  <TableHead>Transactions</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {feeds.map((feed) => (
                  <TableRow key={feed.id} data-testid={`feed-row-${feed.id}`}>
                    <TableCell className="font-medium">{feed.filename}</TableCell>
                    <TableCell>{getClientName(feed.client_id)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{feed.format?.toUpperCase()}</Badge>
                    </TableCell>
                    <TableCell>{feed.imported_count || feed.transactions_count || 0}</TableCell>
                    <TableCell>
                      {feed.status === 'imported' ? (
                        <Badge variant="secondary" className="status-matched">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Imported
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="status-unmatched">
                          <Clock className="w-3 h-3 mr-1" />
                          Pending
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {feed.created_at ? new Date(feed.created_at).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteFeed(feed.id)}
                        data-testid={`delete-feed-${feed.id}`}
                      >
                        <Trash className="w-4 h-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border border-border bg-muted/30">
        <CardContent className="py-4">
          <h4 className="font-medium mb-2">Supported Formats</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm text-muted-foreground">
            <div>
              <span className="font-medium text-foreground">CSV</span> - Comma-separated values. Most banks offer this export option.
            </div>
            <div>
              <span className="font-medium text-foreground">OFX/QFX</span> - Open Financial Exchange. Standard format used by many banks and financial software.
            </div>
            <div>
              <span className="font-medium text-foreground">QIF</span> - Quicken Interchange Format. Legacy format still supported by many banks.
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
